import {
  JSNS,
  type JsonObject,
  type CalTzInfo,
  type DateTimeInfo,
  type Task,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';
import type {
  CalItemMutationResult,
  CalMessage,
  MutationOptions,
} from './calendar';

/* ========================================================================= */
/* Per-command param shapes                                                  */
/* ========================================================================= */

export interface GetTaskParams {
  /** task id (one of id|uid) */
  id?: string;
  /** iCalendar UID (one of id|uid) */
  uid?: string;
  /** also return modified date (`md`) */
  sync?: boolean;
  /** return `<mp>` body parts */
  includeContent?: boolean;
}

export interface CreateTaskParams extends MutationOptions {
  /** message envelope with the VTODO `<inv>`/`<comp>` body */
  message: CalMessage;
}

export interface ModifyTaskParams extends MutationOptions {
  /** default invite id */
  id: string;
  comp: number;
  /** change-sequence of the fetched version (conflict detection) */
  ms: number;
  /** revision of the fetched version */
  rev: number;
  message: CalMessage;
}

export interface CancelTaskParams {
  /** default invite id */
  id: string;
  comp: number;
  /** timezone def referenced by `instance` */
  tz?: CalTzInfo;
  /** cancel a single instance (omit to cancel the whole task) */
  instance?: DateTimeInfo;
  /** range when cancelling instances */
  range?: 'THISANDFUTURE' | 'THISANDPRIOR';
  /** optional cancellation-notice mail */
  notify?: CalMessage;
}

export interface SetTaskParams {
  folderId?: string;
  flags?: string;
  tags?: string;
  noNextAlarm?: boolean;
  nextAlarm?: number;
  /** the default invite */
  default: { ptst?: string; m: CalMessage; [k: string]: unknown };
  /** exception invites */
  except?: Array<{ ptst?: string; m: CalMessage; [k: string]: unknown }>;
  /** cancelled invites */
  cancel?: JsonObject[];
  /** saved replies (empty clears; omit keeps existing) */
  replies?: JsonObject;
}

export interface CreateTaskExceptionParams extends MutationOptions {
  /** default invite id */
  id: string;
  comp: number;
  ms: number;
  rev: number;
  /** RECURRENCE-ID matching the SERIES rule (edit moved exceptions via modifyTask) */
  exceptId: DateTimeInfo;
  message: CalMessage;
}

export interface AddTaskInviteParams {
  /** message envelope (same shape as SetTask `<default>`'s `<m>`) */
  message: CalMessage;
}

export interface RangeFolderParams {
  /** range start (ms epoch) */
  start: number;
  /** range end (ms epoch) */
  end: number;
  folderId?: string;
}

export interface CompleteTaskInstanceParams {
  /** task id */
  id: string;
  /** the instance's RECURRENCE-ID */
  instanceExceptId: DateTimeInfo;
  /** timezone def for `instanceExceptId` */
  tz?: CalTzInfo;
}

/* ========================================================================= */
/* Response shapes                                                           */
/* ========================================================================= */

export interface GetTaskResponse {
  task?: Task[] | Task;
  [k: string]: unknown;
}

export interface SetTaskResponse {
  calItemId?: string;
  default?: { id?: string; [k: string]: unknown };
  except?: Array<{ recurId?: string; id?: string; [k: string]: unknown }>;
  [k: string]: unknown;
}

export interface AddTaskInviteResponse {
  /** omitted if the invite was ignored as outdated */
  calItemId?: string;
  invId?: string;
  compNum?: number;
  [k: string]: unknown;
}

/* ========================================================================= */
/* TasksApi                                                                  */
/* ========================================================================= */

/**
 * Tasks domain (urn:zimbraMail) — VTODO items.
 *
 * Tasks MIRROR appointments: every `<DoAction>AppointmentRequest` has a
 * `<DoAction>TaskRequest` with the same `<inv>`/`<comp>` body, where `<task>`
 * replaces `<appt>`. Task-specific `<comp>` fields are `percentComplete`,
 * `completed`, VTODO `status` (NEED|COMP|INPR|WAITING|DEFERRED) and the due
 * date (via `<e>`/`dueDate`). One typed method per SOAP command — see
 * docs/features/calendar-tasks.md feature 21. Task LISTING via Search
 * (`types=task`) is the mail agent's command; `getTaskSummaries` is the
 * lightweight summary path.
 */
export class TasksApi {
  constructor(private readonly client: ZimbraClient) {}

  /** GetTaskRequest — all invites of a task + replies (mirrors GetAppointment). */
  async getTask(params: GetTaskParams): Promise<GetTaskResponse> {
    const body: JsonObject = {};
    if (params.id !== undefined) body.id = params.id;
    if (params.uid !== undefined) body.uid = params.uid;
    if (params.sync) body.sync = 1;
    if (params.includeContent) body.includeContent = 1;
    return this.client.call<GetTaskResponse>(JSNS.mail, 'GetTask', body);
  }

  /** CreateTaskRequest — create a VTODO (mirrors CreateAppointment). */
  async createTask(params: CreateTaskParams): Promise<CalItemMutationResult> {
    const body: JsonObject = { m: params.message };
    this.applyMutationOptions(body, params);
    return this.client.call<CalItemMutationResult>(JSNS.mail, 'CreateTask', body);
  }

  /** ModifyTaskRequest — edit a task (mirrors ModifyAppointment). */
  async modifyTask(params: ModifyTaskParams): Promise<CalItemMutationResult> {
    const body: JsonObject = {
      id: params.id,
      comp: params.comp,
      ms: params.ms,
      rev: params.rev,
      m: params.message,
    };
    this.applyMutationOptions(body, params);
    return this.client.call<CalItemMutationResult>(JSNS.mail, 'ModifyTask', body);
  }

  /** CancelTaskRequest — cancel the whole task or an instance (mirrors CancelAppointment). */
  async cancelTask(params: CancelTaskParams): Promise<JsonObject> {
    const body: JsonObject = { id: params.id, comp: params.comp };
    if (params.tz) body.tz = params.tz;
    if (params.instance) {
      const inst: JsonObject = { ...params.instance };
      if (params.range) inst.range = params.range;
      body.inst = inst;
    }
    if (params.notify) body.m = params.notify;
    return this.client.call(JSNS.mail, 'CancelTask', body);
  }

  /** SetTaskRequest — set the full task state (mirrors SetAppointment; import path). */
  async setTask(params: SetTaskParams): Promise<SetTaskResponse> {
    const body: JsonObject = { default: params.default };
    if (params.folderId !== undefined) body.l = params.folderId;
    if (params.flags !== undefined) body.f = params.flags;
    if (params.tags !== undefined) body.t = params.tags;
    if (params.noNextAlarm) body.noNextAlarm = 1;
    if (params.nextAlarm !== undefined) body.nextAlarm = params.nextAlarm;
    if (params.except) body.except = params.except;
    if (params.cancel) body.cancel = params.cancel;
    if (params.replies !== undefined) body.replies = params.replies;
    return this.client.call<SetTaskResponse>(JSNS.mail, 'SetTask', body);
  }

  /** CreateTaskExceptionRequest — override one instance (mirrors the appt variant). */
  async createTaskException(params: CreateTaskExceptionParams): Promise<CalItemMutationResult> {
    const inv = { ...(params.message.inv ?? {}), exceptId: params.exceptId };
    const body: JsonObject = {
      id: params.id,
      comp: params.comp,
      ms: params.ms,
      rev: params.rev,
      m: { ...params.message, inv },
    };
    this.applyMutationOptions(body, params);
    return this.client.call<CalItemMutationResult>(JSNS.mail, 'CreateTaskException', body);
  }

  /** AddTaskInviteRequest — add a VTODO invite by UID (mirrors AddAppointmentInvite). */
  async addTaskInvite(params: AddTaskInviteParams): Promise<AddTaskInviteResponse> {
    return this.client.call<AddTaskInviteResponse>(JSNS.mail, 'AddTaskInvite', {
      m: params.message,
    });
  }

  /** GetTaskSummariesRequest — lightweight task summaries (LegacyCalendaringData). */
  async getTaskSummaries(params: RangeFolderParams): Promise<JsonObject> {
    const body: JsonObject = { s: params.start, e: params.end };
    if (params.folderId !== undefined) body.l = params.folderId;
    return this.client.call(JSNS.mail, 'GetTaskSummaries', body);
  }

  /** CompleteTaskInstanceRequest — mark one occurrence of a recurring task complete. */
  async completeTaskInstance(params: CompleteTaskInstanceParams): Promise<JsonObject> {
    const body: JsonObject = { id: params.id, exceptId: params.instanceExceptId };
    if (params.tz) body.tz = params.tz;
    return this.client.call(JSNS.mail, 'CompleteTaskInstance', body);
  }

  /** Apply echo/forcesend options onto an outgoing request body. */
  private applyMutationOptions(body: JsonObject, opts?: MutationOptions): void {
    if (!opts) return;
    if (opts.echo) body.echo = 1;
    if (opts.max !== undefined) body.max = opts.max;
    if (opts.html) body.html = 1;
    if (opts.neuter) body.neuter = 1;
    if (opts.forcesend !== undefined) body.forcesend = opts.forcesend ? 1 : 0;
  }
}
